import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// ページコンポーネント
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import TeacherLogin from './pages/TeacherLogin';
import AuthCallback from './pages/AuthCallback';

// 学生ページ
import StudentDashboard from './pages/student/Dashboard';
import StudentCourses from './pages/student/Courses';
import StudentLessons from './pages/student/Lessons';
import StudentBookings from './pages/student/Bookings';
import StudentBookLesson from './pages/student/BookLesson';
import StudentBillingSetup from './pages/student/BillingSetup';

// 教師ページ
import TeacherDashboard from './pages/teacher/Dashboard';
import TeacherStudents from './pages/teacher/Students';
import TeacherSchedule from './pages/teacher/Schedule';
import TeacherLessons from './pages/teacher/Lessons';

// 管理者ページ
import AdminDashboard from './pages/admin/Dashboard';
import AdminUsers from './pages/admin/Users';
import AdminBookings from './pages/admin/Bookings';
import AdminCourses from './pages/admin/Courses';
import AdminSettings from './pages/admin/Settings';

// 共通コンポーネント
import Layout from './components/Layout';

// 認証が必要なルート
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();
  
  if (loading) {
    return (
      <div className="flex-center" style={{ height: '100vh' }}>
        <div className="loading-spinner" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // ロールに応じた適切なリダイレクト先
    if (user.role === 'admin') {
      return <Navigate to="/admin" />;
    } else if (user.role === 'teacher') {
      return <Navigate to="/teacher" />;
    } else {
      return <Navigate to="/student" />;
    }
  }
  
  return children;
};

function AppRoutes() {
  const { user, isAuthenticated } = useAuth();

  return (
    <Routes>
      {/* 公開ページ */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/teacher-login" element={<TeacherLogin />} />
      <Route path="/auth/success" element={<AuthCallback />} />
      <Route path="/auth/error" element={<AuthCallback />} />
      
      {/* 学生ページ */}
      <Route path="/student" element={
        <ProtectedRoute allowedRoles={['student', 'admin']}>
          <Layout role="student" />
        </ProtectedRoute>
      }>
        <Route index element={<StudentDashboard />} />
        <Route path="book" element={<StudentBookLesson />} />
        <Route path="courses" element={<StudentCourses />} />
        <Route path="lessons/:id" element={<StudentLessons />} />
        <Route path="bookings" element={<StudentBookings />} />
        <Route path="billing" element={<StudentBillingSetup />} />
      </Route>
      
      {/* 教師ページ */}
      <Route path="/teacher" element={
        <ProtectedRoute allowedRoles={['teacher', 'admin']}>
          <Layout role="teacher" />
        </ProtectedRoute>
      }>
        <Route index element={<TeacherDashboard />} />
        <Route path="students" element={<TeacherStudents />} />
        <Route path="schedule" element={<TeacherSchedule />} />
        <Route path="lessons" element={<TeacherLessons />} />
      </Route>
      
      {/* 管理者ページ */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout role="admin" />
        </ProtectedRoute>
      }>
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="bookings" element={<AdminBookings />} />
        <Route path="courses" element={<AdminCourses />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>
      
      {/* デフォルトリダイレクト */}
      <Route path="*" element={
        isAuthenticated 
          ? <Navigate to={user?.role === 'admin' ? '/admin' : user?.role === 'teacher' ? '/teacher' : '/student'} />
          : <Navigate to="/" />
      } />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;
